"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalStorageManager = exports.LocalStorageType = void 0;
const LZString = require("lz-string");
const Utilities_1 = require("./Utilities");
/**
 * Type of storage, also set the current version number below!
 */
var LocalStorageType;
(function (LocalStorageType) {
    LocalStorageType[LocalStorageType["Layout"] = 0] = "Layout";
    LocalStorageType[LocalStorageType["Dashboard"] = 1] = "Dashboard";
    LocalStorageType[LocalStorageType["Clipboard"] = 2] = "Clipboard";
    LocalStorageType[LocalStorageType["History"] = 3] = "History";
    LocalStorageType[LocalStorageType["Splitter"] = 4] = "Splitter";
    LocalStorageType[LocalStorageType["Workspace"] = 5] = "Workspace";
    LocalStorageType[LocalStorageType["SideNav"] = 6] = "SideNav";
})(LocalStorageType = exports.LocalStorageType || (exports.LocalStorageType = {}));
class LocalStorageManager {
    /**
     * Member variables end
     */
    static getItem(localStorageType, key) {
        if (!this.isInitialized())
            this.initialize();
        this.checkCleanUp();
        let itemKey = this.getItemKey(localStorageType, key);
        let item = localStorage.getItem(itemKey);
        if (item) {
            let itemJSON = JSON.parse(item);
            // Check version
            if (itemJSON.version === this.m_LocalStorageTypeVersion.get(localStorageType)) {
                // Update lastAccess
                itemJSON.lastAccess = Date.now();
                localStorage.setItem(itemKey, JSON.stringify(itemJSON));
                return LZString.decompressFromUTF16(itemJSON.value);
            }
            else {
                // Run cleanup due to version change
                this.cleanUp();
                return undefined;
            }
        }
        else {
            return undefined;
        }
    }
    static setItem(localStorageType, key, value, canDelete = false) {
        if (!this.isInitialized())
            this.initialize();
        this.checkCleanUp();
        try {
            let localStorageItem = {
                version: this.m_LocalStorageTypeVersion.get(localStorageType),
                canDelete,
                lastAccess: Date.now(),
                value: LZString.compressToUTF16(value),
            };
            localStorage.setItem(this.getItemKey(localStorageType, key), JSON.stringify(localStorageItem));
        }
        catch (error) {
            this.handleError(error, () => { LocalStorageManager.setItem(localStorageType, key, value, canDelete); });
        }
    }
    static removeItem(localStorageType, key) {
        if (!this.isInitialized())
            this.initialize();
        let itemKey = this.getItemKey(localStorageType, key);
        localStorage.removeItem(itemKey);
    }
    static getItemKey(localStorageType, externalKey) {
        let userInfo = Utilities_1.synUtil.UserInfo.getUserInfo();
        let instanceId = userInfo.Fields["cgdocument.instanzid"];
        let userId = userInfo.Id;
        return "LSM_" + instanceId + "_" + userId + "_" + LocalStorageType[localStorageType] + "_" + externalKey;
    }
    static getLocalStorageTypeFromItemKey(itemKey) {
        let itemType = itemKey.split("_")[3];
        for (let type in LocalStorageType) {
            if (isNaN(Number(type))) {
                if (type === itemType) {
                    return LocalStorageType[itemType];
                }
            }
        }
        return undefined;
    }
    static isInitialized() {
        // Check if all LocalStorageTypes have versions (For developers)
        for (let type in LocalStorageType) {
            if (Number(type)) {
                if (!this.m_LocalStorageTypeVersion.get(Number(type)))
                    alert("LocalStorageType." + LocalStorageType[type] + " has no version. Please fix in LocalStorageManager.ts");
            }
        }
        window.onstorage = function (e) {
            // BUG3157
            // workaround for IE11/Edge HTML localstorage synchronization problem.
            // must be left empty
            // https://stackoverflow.com/questions/24077117/localstorage-in-win8-1-ie11-does-not-synchronize
            // https://github.com/JillElaine/jquery-idleTimeout/issues/50
            // https://web.archive.org/web/20171208014524/https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/110656/
        };
        // Check for initialization
        let metaData = localStorage.getItem(this.m_LSMMetaDataKey);
        if (metaData || this.m_IsInitializing)
            return true;
        else
            return false;
    }
    static initialize() {
        this.m_IsInitializing = true;
        this.migrate();
        // Finish initialization by setting LSMMetaData
        let LSMMetaData = {
            lastCleanup: Date.now(),
        };
        localStorage.setItem(this.m_LSMMetaDataKey, JSON.stringify(LSMMetaData));
        this.m_IsInitializing = false;
    }
    // Calls cleanUp if last one is longer than 15 days ago
    static checkCleanUp() {
        if (!this.m_IsInitializing) {
            let metaDataJSON = JSON.parse(localStorage.getItem(this.m_LSMMetaDataKey));
            if (Date.now() - metaDataJSON.lastCleanup >= 15 * 24 * 60 * 60 * 1000) {
                this.cleanUp();
            }
        }
    }
    // Cleans up deleted LocalStorageTypes and outdated versions
    static cleanUp() {
        for (let i = localStorage.length - 1; i >= 0; --i) {
            let localStorageKey = localStorage.key(i);
            if (localStorageKey.startsWith("LSM_")) {
                let storageType = this.getLocalStorageTypeFromItemKey(localStorageKey);
                if (storageType != undefined) {
                    let itemJSON = JSON.parse(localStorage.getItem(localStorageKey));
                    // Check version
                    if (itemJSON.version !== this.m_LocalStorageTypeVersion.get(parseInt(storageType))) {
                        localStorage.removeItem(localStorageKey);
                        // eslint-disable-next-line no-console
                        console.warn("LSM: Item version outdated for StorageType: " + LocalStorageType[storageType] + ". Item deleted: " + localStorageKey);
                    }
                }
                else {
                    // StorageType no longer exists
                    localStorage.removeItem(localStorageKey);
                    // eslint-disable-next-line no-console
                    console.warn("LSM: StorageType no longer exists: " + localStorageKey.split("_")[3] + ". Item deleted: " + localStorageKey);
                }
            }
        }
        // Set last cleanup date
        let metaDataJSON = JSON.parse(localStorage.getItem(this.m_LSMMetaDataKey));
        metaDataJSON.lastCleanup = Date.now();
        localStorage.setItem(this.m_LSMMetaDataKey, JSON.stringify(metaDataJSON));
    }
    static handleError(error, callback) {
        // Check if local storage full
        let quotaExceeded = false;
        if (error) {
            if (error.code) {
                switch (error.code) {
                    case 22:
                        quotaExceeded = true;
                        break;
                    case 1014:
                        // Firefox
                        if (error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
                            quotaExceeded = true;
                        }
                        break;
                }
            }
        }
        // Delete items with canDelete and oldest lastAccess
        if (quotaExceeded) {
            // eslint-disable-next-line no-console
            console.warn("LSM: LocalStorage full. Attempting to free space.");
            // Force cleanup
            this.cleanUp();
            // Free additional space
            let compareLastAccess = function (a, b) {
                if (a.lastAccess < b.lastAccess)
                    return -1;
                if (a.lastAccess > b.lastAccess)
                    return 1;
                return 0;
            };
            let canDeleteItems = [];
            for (let i = 0; i < localStorage.length; i++) {
                if (localStorage.key(i).startsWith("LSM_")) {
                    let itemJSON = JSON.parse(localStorage.getItem(localStorage.key(i)));
                    if (itemJSON.canDelete) {
                        canDeleteItems.push({
                            key: localStorage.key(i),
                            lastAccess: itemJSON.lastAccess,
                        });
                    }
                }
            }
            if (canDeleteItems.length > 0) {
                canDeleteItems.sort(compareLastAccess);
                // Delete 10% of items (sorted by oldest lastAccess date), always delete at least one
                let oneTenth = Math.floor(canDeleteItems.length / 10);
                if (oneTenth <= 1) {
                    localStorage.removeItem(canDeleteItems[0].key);
                    // eslint-disable-next-line no-console
                    console.warn("LSM: Removed one item: " + canDeleteItems[0].key);
                }
                else {
                    for (let i = 0; i < oneTenth; i++)
                        localStorage.removeItem(canDeleteItems[i].key);
                    // eslint-disable-next-line no-console
                    console.warn("LSM: Removed " + oneTenth + " items.");
                }
                // Retry action after freeing up space
                callback();
            }
            else
                // eslint-disable-next-line no-console
                console.error("LSM: LocalStorage out of space. No items set to canDelete, unable to free up space. New item hasn't been saved!");
        }
    }
    // Migrates old localStorage data
    static migrate() {
        // Loop over all localStorage items and check if they can get migrated
        for (let i = localStorage.length - 1; i >= 0; --i) {
            let localStorageKey = localStorage.key(i);
            if (localStorageKey) {
                if (localStorageKey.startsWith("LSM_")) {
                    // Do nothing, already migrated (May have been interrupted)
                }
                else {
                    if (localStorageKey === "lastCleanup_layout") {
                        // Layoutstorage
                        localStorage.removeItem(localStorageKey);
                    }
                    else if (localStorageKey.startsWith("layout_")) {
                        // Layoutstorage
                        let layoutMetaData = localStorage.getItem("metaData_" + localStorageKey);
                        if (layoutMetaData) {
                            let layoutMetaDataJSON = JSON.parse(layoutMetaData);
                            if (layoutMetaDataJSON.version === "1.2") {
                                let layoutDataJSON = JSON.parse(LZString.decompressFromUTF16(localStorage.getItem(localStorageKey)));
                                let layoutNewJSON = {
                                    localEtag: layoutMetaDataJSON.localEtag,
                                    layoutJSON: layoutDataJSON,
                                };
                                localStorage.removeItem(localStorageKey);
                                localStorage.removeItem("metaData_" + localStorageKey);
                                this.setItem(LocalStorageType.Layout, localStorageKey.split("_").pop(), JSON.stringify(layoutNewJSON), true);
                            }
                            else {
                                // Outdated version, no migration
                                localStorage.removeItem(localStorageKey);
                                localStorage.removeItem("metaData_" + localStorageKey);
                            }
                        }
                        else
                            localStorage.removeItem(localStorageKey);
                    }
                    else if (localStorageKey.startsWith(Utilities_1.synUtil.UserInfo.getUserInfo().Id + "_dashboard_")) {
                        // Dashboard
                        let key = localStorageKey.split("_")[2];
                        let value = localStorage.getItem(localStorageKey);
                        localStorage.removeItem(localStorageKey);
                        this.setItem(LocalStorageType.Dashboard, key, value);
                    }
                    else if (localStorageKey.startsWith(Utilities_1.synUtil.UserInfo.getUserInfo().Id + "_")) {
                        // Splitter (Per view)
                        let key = localStorageKey.substring(localStorageKey.indexOf("_") + 1);
                        let value = localStorage.getItem(localStorageKey);
                        localStorage.removeItem(localStorageKey);
                        this.setItem(LocalStorageType.Splitter, key, value);
                    }
                    else if (localStorageKey === "clipboardItems") {
                        // Clipboard
                        let key = localStorageKey;
                        let value = localStorage.getItem(localStorageKey);
                        localStorage.removeItem(localStorageKey);
                        this.setItem(LocalStorageType.Clipboard, key, value);
                    }
                    else if (localStorageKey === "forceWorkspaceDisabled") {
                        // Workspace
                        let key = localStorageKey;
                        let value = localStorage.getItem(localStorageKey);
                        localStorage.removeItem(localStorageKey);
                        this.setItem(LocalStorageType.Workspace, key, value);
                    }
                    else if (localStorageKey === "historyItems") {
                        // History
                        let key = localStorageKey;
                        let value = localStorage.getItem(localStorageKey);
                        localStorage.removeItem(localStorageKey);
                        this.setItem(LocalStorageType.History, key, value);
                    }
                }
            }
        }
    }
}
exports.LocalStorageManager = LocalStorageManager;
/**
 * Member variables start
 */
// Current version number for LocalStorageTypes (Changing the version number leads to data deletion of old versions)
LocalStorageManager.m_LocalStorageTypeVersion = new Map([
    [LocalStorageType.Layout, 1],
    [LocalStorageType.Dashboard, 1],
    [LocalStorageType.Clipboard, 1],
    [LocalStorageType.History, 1],
    [LocalStorageType.Splitter, 1],
    [LocalStorageType.Workspace, 1],
    [LocalStorageType.SideNav, 1],
]);
LocalStorageManager.m_IsInitializing = false;
LocalStorageManager.m_LSMMetaDataKey = "MetaData_LSM";
